import { Box, Button, ButtonOwnProps, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import React, { FC, ReactNode, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import BlockIcon from "@mui/icons-material/Block";
import DevicesIcon from "@mui/icons-material/Devices";
import MarginIcon from "@mui/icons-material/Margin";
import ConstructionIcon from "@mui/icons-material/Construction";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VideocamIcon from "@mui/icons-material/Videocam";
import PodcastsIcon from "@mui/icons-material/Podcasts";

import "./nestedPageLayout.css";
import { useGetSiteConfigQuery } from "../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../utils/commonUtils";
import { CONFIG_MODE_E } from "../../utils/constants";
import { useAppSelector } from "../../redux";
import { usePageLayoutContext } from "../PageLayout";

const DATA_MANAGEMENT_PATH_KEY = "data-management";
const CONFIGURATION_PATH_KEY = "configuration";

const USER_MANAGEMENT_TAB_KEY = "user-management";
const ADDITIONAL_SETTINGS_TAB_KEY = "additional-settings";

interface ITabButtonDetails {
	key: string;
	name: string;
	icon: ReactNode;
	color?: ButtonOwnProps["color"];
}

type NestedPageLayoutContextType = {
	forceSyncStatusLoading: boolean;
	startSyncStatusPolling: () => void;
};

const DATA_MANAGEMENT_TABS_LIST: ITabButtonDetails[] = [
	{
		key: "authorized-list",
		name: "Authorized List",
		icon: <PlaylistAddCheckIcon />,
		color: "success"
	},
	{
		key: "blacklist",
		name: "Blacklist",
		icon: <BlockIcon />,
		color: "error"
	}
];

const CONFIGURATION_TABS_LIST: ITabButtonDetails[] = [
	{
		key: "system",
		name: "System Settings",
		icon: <MarginIcon />
	},
	{
		key: "camera-settings",
		name: "Camera Settings",
		icon: <VideocamIcon />
	},
	{
		key: "additional-settings",
		name: "Additional Settings",
		icon: <ConstructionIcon />
	},
	{
		key: "user-management",
		name: "User Management",
		icon: <SupervisedUserCircleIcon />
	},
	{
		key: "devices",
		name: "Device Status",
		icon: <DevicesIcon />
	},
	{
		key: "network-settings",
		name: "Network Settings",
		icon: <PodcastsIcon />
	}
];

const NestedPageLayout: FC = () => {
	const { forceSyncStatusLoading, startSyncStatusPolling } = usePageLayoutContext();

	const location = useLocation();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const locationSplit = location.pathname.split("/");

	function handleChangeTab(newTab: string) {
		if (locationSplit[1] === DATA_MANAGEMENT_PATH_KEY) {
			navigate(`/${DATA_MANAGEMENT_PATH_KEY}/${newTab}`, { replace: true });
		} else if (locationSplit[1] === CONFIGURATION_PATH_KEY) {
			navigate(`/${CONFIGURATION_PATH_KEY}/${newTab}`, { replace: true });
		}
	}

	function getTabsList(): ITabButtonDetails[] {
		if (locationSplit[1] === DATA_MANAGEMENT_PATH_KEY) return DATA_MANAGEMENT_TABS_LIST;

		if (locationSplit[1] === CONFIGURATION_PATH_KEY) {
			const restrictedTabs: string[] = [];

			if (RestrictedAccess.includes(RESTRICTED_ACCESS_E.USERS_PAGE)) {
				restrictedTabs.push(USER_MANAGEMENT_TAB_KEY);
			}

			if (RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADDITIONAL_SETTINGS_PAGE)) {
				restrictedTabs.push(ADDITIONAL_SETTINGS_TAB_KEY);
			}

			return CONFIGURATION_TABS_LIST.filter((item) => !restrictedTabs.includes(item.key));
		}
		return [];
	}

	useEffect(() => {
		if (locationSplit[1] === DATA_MANAGEMENT_PATH_KEY) {
			if (siteConfig && siteConfig.mode === CONFIG_MODE_E.MY_GATE) {
				navigate("/dashboard");
			}
		}
	}, [locationSplit, navigate, siteConfig]);

	const activeTab = locationSplit[locationSplit.length - 1];

	const tabsList = getTabsList();

	return (
		<Box className="data-management-page-layout-wrapper">
			{tabsList.findIndex((tabItem) => tabItem.key === activeTab) >= 0 ? (
				<Box
					className="data-management-page-layout-header"
					sx={{
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						gap: { xs: "0.75rem", sm: "1rem" },
						padding: { xs: "0.75rem", sm: "1rem" }
					}}
				>
					{/* Mobile: Dropdown/Select for tab selection */}
					<Box sx={{ display: { xs: "block", sm: "none" }, width: "100%" }}>
						<FormControl fullWidth size="small">
							<InputLabel
								sx={{
									fontSize: { xs: "0.875rem", sm: "1rem" },
									fontWeight: 500
								}}
							>
								Select Section
							</InputLabel>
							<Select
								value={activeTab}
								onChange={(e) => handleChangeTab(e.target.value)}
								label="Select Section"
								IconComponent={KeyboardArrowDownIcon}
								sx={{
									borderRadius: "8px",
									background: "rgba(255, 255, 255, 0.9)",
									"& .MuiSelect-select": {
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										fontSize: "0.875rem",
										fontWeight: 500
									},
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(0, 0, 0, 0.12)"
									},
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(0, 0, 0, 0.24)"
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: "primary.main"
									}
								}}
							>
								{tabsList.map((tabItem) => (
									<MenuItem
										key={tabItem.key}
										value={tabItem.key}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: "0.5rem",
											fontSize: "0.875rem",
											padding: "8px 12px"
										}}
									>
										<Box
											sx={{
												color:
													tabItem.color === "success"
														? "success.main"
														: tabItem.color === "error"
														? "error.main"
														: "primary.main",
												display: "flex",
												alignItems: "center"
											}}
										>
											{tabItem.icon}
										</Box>
										<Typography sx={{ fontWeight: 500 }}>{tabItem.name}</Typography>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>

					{/* Desktop: Original button tabs */}
					<Box
						sx={{
							display: { xs: "none", sm: "flex" },
							flexWrap: "wrap",
							gap: "0.75rem"
						}}
					>
						{tabsList.map((tabItem) => (
							<Button
								key={tabItem.key}
								disableElevation
								variant={activeTab === tabItem.key ? "contained" : "outlined"}
								color={tabItem.color ? tabItem.color : "primary"}
								startIcon={tabItem.icon}
								onClick={() => handleChangeTab(tabItem.key)}
								size="large"
							>
								{tabItem.name}
							</Button>
						))}
					</Box>
				</Box>
			) : null}

			<Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", height: 0 }}>
				<Outlet context={{ forceSyncStatusLoading, startSyncStatusPolling }} />
			</Box>
		</Box>
	);
};

export default NestedPageLayout;

export function useNestedPageLayoutContext() {
	return useOutletContext<NestedPageLayoutContextType>();
}
